import { useState, useCallback } from 'react'
import { technicalCardAPI, estimateAPI } from '../services/api'
import toast from 'react-hot-toast'

export const useTechnicalCard = () => {
  const [card, setCard] = useState(null)
  const [sheets, setSheets] = useState([])
  const [estimates, setEstimates] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const uploadCard = useCallback(async (file) => {
    setLoading(true)
    setError(null)
    try {
      const response = await technicalCardAPI.upload(file)
      setCard(response.data)
      toast.success('Техническая карта загружена')
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message
      setError(errorMessage)
      toast.error('Ошибка загрузки технической карты')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchSheets = useCallback(async (cardId) => {
    setLoading(true)
    try {
      const response = await technicalCardAPI.getSheets(cardId)
      setSheets(response.data.sheets || [])
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message
      setError(errorMessage)
      toast.error('Ошибка загрузки страниц')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const classifySheet = useCallback(async (sheetId, sheetType) => {
    try {
      const response = await technicalCardAPI.classifySheet(sheetId, {
        sheet_id: sheetId,
        sheet_type: sheetType,
      })

      // Обновляем листы локально (без перезагрузки)
      setSheets(prevSheets => prevSheets.map(s =>
        s.id === sheetId ? { ...s, ...response.data } : s
      ))

      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message
      toast.error(errorMessage)
      throw err
    }
  }, [])

  const analyzeSheet = useCallback(async (sheetId) => {
    try {
      const response = await technicalCardAPI.analyzeSheet(sheetId)

      // Обновляем листы локально с результатами анализа (без перезагрузки)
      setSheets(prevSheets => prevSheets.map(s =>
        s.id === sheetId ? { ...s, ...response.data } : s
      ))

      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message
      toast.error(errorMessage)
      throw err
    }
  }, [])

  const fetchEstimates = useCallback(async (cardId) => {
    try {
      const response = await estimateAPI.getEstimates(cardId)
      setEstimates(response.data.estimates || [])
      return response.data.estimates
    } catch (err) {
      toast.error('Ошибка загрузки смет')
      throw err
    }
  }, [])

  const verifyEstimate = useCallback(async (cardId, estimateType) => {
    setLoading(true)
    try {
      const response = await estimateAPI.verify({
        technical_card_id: cardId,
        estimate_type: estimateType,
      })
      toast.success('Верификация завершена')
      return response.data
    } catch (err) {
      toast.error('Ошибка верификации')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Обновить конкретный лист (используется для WebSocket обновлений)
  const updateSheet = useCallback((sheetId, updatedData) => {
    setSheets(prevSheets => prevSheets.map(s =>
      s.id === sheetId ? { ...s, ...updatedData } : s
    ))
  }, [])

  // Обновить все листы (используется для WebSocket обновлений)
  const updateSheets = useCallback((updatedSheets) => {
    setSheets(updatedSheets)
  }, [])

  return {
    card,
    sheets,
    estimates,
    loading,
    error,
    uploadCard,
    fetchSheets,
    classifySheet,
    analyzeSheet,
    fetchEstimates,
    verifyEstimate,
    updateSheet,
    updateSheets,
  }
}
